import React, { useContext, useMemo } from 'react';
import { Box, Button, Flex, Icon } from '@fullstory/components';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import { defaultOrControlLimit } from '@fullstory/search-shared-ui';
import { PlatformSelector, NativePlatform } from '@fullstory/proto/fs/api/segment';
import { SelectorInputTree } from './SelectorInputTree';
import { ValidateNamedElementResponse } from '@fullstory/proto/fs/services/dx-objects';
import { SelectorRule, parseSelector, serializeSelector } from '@fullstory/selectors';
import { ElementApiNameAttribute } from '@fullstory/client-api';
import { FormattedMessage, useIntl } from 'react-intl';
import { ApiSelectorInput } from './ApiSelectorInput';
import {
  AppearancesContext,
  AppearancesProps,
  useDefaultAppearancesPropertyOptions,
  OnPreviewFn,
  ActiveIndexContext,
  appearancesProperty,
} from '@fullstory/signal-studio-context';
import { makePlatformSelectorDeps } from '../../../utils';
import { PlatformSelectorConfig } from '../../../types';

const additionalAppearancesPropertyOptions: appearancesProperty[] = ['text', 'element'];

export enum SelectorInputSource {
  elementModal,
  propertyModal,
}

interface MultiPlatformSelectorInputsProps {
  firstSelectorRef?: React.Ref<HTMLTextAreaElement>;
  selectors: PlatformSelectorConfig[];
  apiName: string;
  savedApiName: string;
  validationStates: ValidateNamedElementResponse.ValidationState[];
  onChange: (selectors: PlatformSelectorConfig[], apiName: string) => void;
  onHoverSelector: (selector?: string) => void;
  disabled?: boolean;
  source: SelectorInputSource;
  trimTrigger?: boolean;
  setTrimTrigger?: (off: boolean) => void;
}

export const createApiElementSelector = (apiName: string) => {
  const parsed = parseSelector(`[${ElementApiNameAttribute}="${apiName}"]`);
  const selector = serializeSelector(parsed);
  return { selector, originalSelector: selector, canonicalizedSelector: null, platforms: [] };
};

export const extractApiElementSelector = (
  selectors: PlatformSelector[],
  apiName: string,
): {
  selector?: PlatformSelector;
  index?: number;
} => {
  // The api selector is the dedicated API attribute selector and the initialPlatformSels are the rest
  const selectorIndex = selectors.findIndex(
    sel => sel.selector === createApiElementSelector(apiName).selector,
  );

  if (selectorIndex !== -1) {
    return { selector: selectors[selectorIndex]!, index: selectorIndex };
  } else {
    if (apiName) {
      console.error(
        `Could not find api selector for ${apiName}. Selectors: ${JSON.stringify(selectors)}`,
      );
    }
    return {};
  }
};

const SelectorAppearances = ({
  Appearances,
  selector,
  onPreview,
}: {
  Appearances: React.ComponentType<AppearancesProps>;
  selector: PlatformSelector;
  onPreview?: OnPreviewFn;
}) => {
  const intl = useIntl();
  const event = useMemo(
    () => ({ primary: null, deps: makePlatformSelectorDeps(selector) }),
    [selector],
  );
  const supportsMobile =
    !selector.platforms.length ||
    selector.platforms.some(
      p => p === NativePlatform.NATIVE_PLATFORM_IOS || p === NativePlatform.NATIVE_PLATFORM_ANDROID,
    );
  const appearancesPropertyOptions = useDefaultAppearancesPropertyOptions({
    supportsMobile,
    additional: additionalAppearancesPropertyOptions,
  });

  return (
    <Appearances
      data-component="SelectorAppearances"
      event={event}
      propertyOptions={appearancesPropertyOptions}
      onPreview={onPreview}
      objectType={intl.formatMessage({ defaultMessage: 'selector', id: 'A7ml5w' })}
      supportsMobile={supportsMobile}
    />
  );
};

export const MultiPlatformSelectorInputs: React.FC<MultiPlatformSelectorInputsProps> = ({
  firstSelectorRef,
  selectors,
  apiName,
  savedApiName,
  validationStates,
  onChange,
  onHoverSelector,
  disabled,
  source,
  trimTrigger,
  setTrimTrigger,
}) => {
  const flags = useFlags();
  const webOnly = !flags.hasBit(Flag.FeatNativeMobile);
  // The API name is immutable, so special handling if this is already saved, i.e, don't let the user change it
  const isSavedApiElement = !!savedApiName;
  const SelectorInputComponent = SelectorInputTree;

  const { index: apiIndex } = useMemo(
    () => extractApiElementSelector(selectors, apiName ?? ''),
    [selectors, apiName],
  );
  const activeSelector = useContext(ActiveIndexContext);
  const appearances = useContext(AppearancesContext);
  const { Appearances, onPreview } = { ...appearances };

  return (
    <Box data-component="MultiPlatformSelectorInputs">
      {selectors.map((sel, i) => (
        <Box key={i} marginBottom={source === SelectorInputSource.elementModal ? 24 : 8}>
          {i === apiIndex ? (
            <ApiSelectorInput
              apiName={apiName ?? ''}
              editable={!isSavedApiElement && !disabled}
              onChange={apiName => {
                const newSelectors = selectors.slice();
                const newApiSelector = createApiElementSelector(apiName);
                newSelectors.splice(apiIndex, 1, newApiSelector);
                onChange(newSelectors, apiName);
              }}
              onDelete={
                selectors.length > 1
                  ? () => {
                      const newSelectors = selectors.slice();
                      newSelectors.splice(i, 1);
                      onChange(newSelectors, '');
                      if (
                        activeSelector &&
                        (activeSelector.index > i || activeSelector.index === newSelectors.length)
                      ) {
                        activeSelector.setIndex(activeSelector.index - 1);
                      }
                    }
                  : undefined
              }
              onFocus={() => {
                activeSelector?.setIndex(i);
              }}
              active={i === activeSelector?.index}
              Appearances={
                SelectorRule.isValid(sel.selector) && apiName && Appearances ? (
                  <Box>
                    <SelectorAppearances
                      Appearances={Appearances}
                      selector={sel}
                      onPreview={
                        onPreview
                          ? (primary, refinements, _) => onPreview(primary, refinements, _, i)
                          : undefined
                      }
                    />
                  </Box>
                ) : undefined
              }
            />
          ) : (
            <SelectorInputComponent
              innerRef={i === 0 ? firstSelectorRef : undefined}
              selector={sel}
              validationState={validationStates[i]}
              disabled={disabled}
              onChange={newSel => {
                const newSelectors = selectors.slice();
                newSelectors[i] = newSel;
                onChange(newSelectors, apiName);
              }}
              onDelete={
                selectors.length > 1
                  ? () => {
                      const newSelectors = selectors.slice();
                      newSelectors.splice(i, 1);
                      onChange(newSelectors, apiName);
                      if (
                        activeSelector &&
                        (activeSelector.index > i || activeSelector.index === newSelectors.length)
                      ) {
                        activeSelector.setIndex(activeSelector.index - 1);
                      }
                    }
                  : undefined
              }
              onFocus={() => {
                activeSelector?.setIndex(i);
              }}
              onHoverSelector={onHoverSelector}
              active={i === activeSelector?.index}
              testId={`element-selector-text-input-${i}-text-input`}
              Appearances={
                SelectorRule.isValid(sel.selector) && Appearances ? (
                  <Box>
                    <SelectorAppearances
                      Appearances={Appearances}
                      selector={sel}
                      onPreview={
                        onPreview
                          ? (primary, refinements, _) => onPreview(primary, refinements, _, i)
                          : undefined
                      }
                    />
                  </Box>
                ) : undefined
              }
              trimTrigger={trimTrigger && i === activeSelector?.index}
              setTrimTrigger={setTrimTrigger}
            />
          )}
        </Box>
      ))}
      {selectors.length < defaultOrControlLimit && !disabled && (
        <Flex justifyContent="flex-start">
          <Button
            testId="or-button"
            variant="outline"
            disabled={disabled}
            onClick={
              !disabled
                ? () => {
                    const newSelectors = selectors.slice();
                    newSelectors.push({
                      selector: '',
                      platforms: webOnly ? [NativePlatform.NATIVE_PLATFORM_WEB] : [],
                      originalSelector: '',
                      canonicalizedSelector: null,
                    });
                    onChange(newSelectors, apiName);
                    activeSelector?.setIndex(newSelectors.length - 1);
                  }
                : undefined
            }
          >
            <Flex gap={8}>
              <Icon name="plus" />
              <FormattedMessage
                defaultMessage="Add selector"
                id="UwO7ct"
                description="Add a CSS selector to the element definition."
              />
            </Flex>
          </Button>
          {apiIndex === undefined && source == SelectorInputSource.elementModal && (
            <Button
              testId="add-api-button"
              variant="minimal"
              disabled={disabled}
              onClick={
                !disabled
                  ? () => {
                      const apiSelector = createApiElementSelector('');
                      const newSelectors = [...selectors, apiSelector];
                      onChange(newSelectors, apiName);
                      activeSelector?.setIndex(newSelectors.length - 1);
                    }
                  : undefined
              }
            >
              <FormattedMessage
                defaultMessage="Add API Attribute"
                id="/5ULAZ"
                description="Add an API attribute to the element definition."
              />
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
};
